import { KNOWN_FILE_EXTENSIONS } from './constants'

/**
 * Converts a word to sentence case.
 *
 * @example
 * toSentenceCase('hELLo') // 'Hello'
 */
export function toSentenceCase (input: string): string {
  return `${input.charAt(0).toUpperCase()}${input.slice(1).toLowerCase()}`
}

/**
 * Lowercases the first letter to help convert a word to camel case.
 *
 * @example
 * toSentenceCase('hELLo') // 'hELLo'
 */
export function toCamelCase (input: string): string {
  return `${input.charAt(0).toLowerCase()}${input.slice(1)}`
}

/**
 * Converts a word to kebab case.
 *
 * @example
 * toKebabCase('hELLo World') // 'hello-world'
 */
export function toKebabCase (input: string): string {
  return input.replace(/[_\s]/g, '-').toLowerCase()
}

/**
 * Converts a sentence to sentence case.
 *
 * @example
 * formatSentence('hELLo wOrLd') // 'Hello World'
 */
export function formatSentence (input: string): string {
  return input.replace(/_/g, ' ').replace(/\w*/g, toSentenceCase)
}

/**
 * Tests whether a value matches any regex or string in an array.
 *
 * @param testVal string to test
 * @param targetArray target array to check for existence of @testVal
 * @returns boolean
 */
export function containedIn (testVal: string, targetArray: (string | RegExp)[]): boolean {
  return targetArray.some((item: string | RegExp) => {
    return typeof item === 'string'
      ? testVal.includes(item)
      : item.test(testVal)
  })
}

/**
 * Creates a numeric hash from a string.
 *
 * See {@link https://stackoverflow.com/a/7616484}.
 */
export function hashString (str: string): number {
  let hash = 0
  let i
  let chr
  if (str.length === 0) return hash
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + chr
    hash |= 0
  }
  return hash
}

/**
 * Normalizes a path with `_` by replacing them with `.`.
 * Useful when dealing with the `k` path from {@link APIPayloadResponse} objects.
 */
export function normalizePath (path: string): string {
  if (typeof path !== 'string') return ''
  return path.replace(/_/g, '.')
}

/**
 * Converts a screaming snake case string to camel case.
 *
 * @example
 * toCamelCase('HELLO_WORLD') // 'helloWorld'
 */
export function screamingSnakeToCamelCase (input: string): string {
  if (typeof input !== 'string') return ''
  const words = input.toLowerCase().split('_')
  const camelCaseWords = words.map((word, index) => {
    if (index === 0) {
      return word
    } else {
      return toSentenceCase(word)
    }
  })
  return camelCaseWords.join('')
}

/**
 * Converts a camel case string to screaming snake case.
 *
 * @example
 * toCamelCase('helloWorld') // 'HELLO_WORLD'
 */
export function camelCaseToScreamingSnakeCase (input: string): string {
  if (typeof input !== 'string') return ''
  return input.replace(/([A-Z])/g, '_$1').toUpperCase()
}

/**
 * Converts a camel case string to snake case.
 *
 * @example
 * toCamelCase('helloWorld') // 'hello_world'
 */
export function camelCaseToSnakeCase (input: string): string {
  if (typeof input !== 'string') return ''
  return input.replace(/([A-Z])/g, '_$1').toLowerCase()
}

/**
 * Converts a camel case string to kebab case.
 *
 * @example
 * toCamelCase('helloWorld') // 'hello-world'
 */
export function camelCaseToKebabCase (input: string): string {
  if (typeof input !== 'string') return ''
  return input.replace(/([A-Z])/g, '-$1').toLowerCase()
}

/**
 * Validates if a string is a valid email format.
 *
 * @example
 * isValidEmail('test@example.com') // true
 */
export function isValidEmail (email: string | null): boolean {
  if (typeof email !== 'string') return false
  const emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return emailPattern.test(email)
}

/**
 * Determines display name of a file by removing the extension.
 * Supports multipart extensions by checking against a set of known extensions.
 *
 * @example
 * ('archive.tar.gz') // ['archive']
 * ('Document 1x.pdf') // ['Document 1x']
 * ('.hidden') // ['.hidden']
 * ['noextension'] // ['noextension']
 */
export function normalizeFileName (
  fileName: string,
  extensionParts: Set<string> = KNOWN_FILE_EXTENSIONS,
): string {
  // ensure extension parts are split by '.' and stored for O(1) lookup
  const processedExtensionParts = new Set(Array.from(extensionParts).flatMap((ext: string) => ext.split('.')))
  const segments = fileName.split('.')
  if (segments.length <= 1) return fileName
  // iterate backwards to find the longest matching extension
  let extensionStartIndex = segments.length - 1
  while (
    extensionStartIndex > 0 &&
    processedExtensionParts.has(segments[extensionStartIndex])
  ) {
    extensionStartIndex--
  }
  return segments.slice(0, extensionStartIndex + 1).join('.')
}
